import React, {
  useRef,
  useCallback,
  useState,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { DateTime } from "luxon";
import { useHistory } from "react-router-dom";
import { UserContext, RoleType } from "app/contexts/UserContext";

import { formatFullWeekdayDate } from "app/utils/luxonFormats";
import useCalendarPreferences from "app/utils/hooks/useCalendarPreferences";
import { CalendarViewType } from "app/models/CalendarPreferences";
import useCurrentFacility from "app/utils/hooks/scheduling/useCurrentFacility";

import { Loading } from "app/components/Wrappers";
import CalendarToolbar from "app/routes/Calendar/CalendarToolbar";
import FacilityCalendarView from "app/routes/Calendar/views/FacilityCalendarView";
import { CoachProfileModel } from "app/models/scheduling/CoachProfileModel";
import styles from "app/routes/Calendar/styles.module.scss";
import {
  COMPARE_COACH_STORAGE_KEY,
  readStoredResourceView,
  readStoredValue,
  storeResourceView,
  writeStoredValue,
} from "./facilityView";

// The Coaches view orders its columns by `order` and then by `title`, so the
// first coach of this list is the first column there.
function sortCoachesForCalendar(
  coachProfiles: CoachProfileModel[],
): CoachProfileModel[] {
  return [...coachProfiles].sort(
    (a, b) =>
      (a.orderNumber ?? Number.MAX_SAFE_INTEGER) -
        (b.orderNumber ?? Number.MAX_SAFE_INTEGER) ||
      a.fullName.localeCompare(b.fullName),
  );
}

export default function FacilityCalendarPage() {
  const calendarRef = useRef(null);
  const eventElRef = useRef(null);
  const initNewEventOverrideRef = useRef<(() => void) | null>(null);
  const history = useHistory();
  const user = useContext(UserContext);
  const isAdminOrScheduler = user.role === RoleType.ADMIN || user.role === RoleType.SCHEDULER;
  const currentFacility = useCurrentFacility();
  const { preferences, refresh } = useCalendarPreferences();

  useEffect(() => {
    refresh();
  }, [refresh]);

  const timeZone = currentFacility.data?.timeZone || DateTime.now().zoneName;

  const [calendarDate, setCalendarDate] = useState<DateTime>(null);

  // Only when the facility has the feature on: the resources tables ship in
  // the same release, and the deployer runs the migration after the deploy.
  const resourceManagementEnabled =
    currentFacility.data?.featureFlags?.resourceManagement === true;

  const [storedResourceView, setStoredResourceView] = useState<boolean>(
    readStoredResourceView,
  );
  const resourceView = storedResourceView && resourceManagementEnabled;

  // `undefined` means no choice yet. The effect below sets a coach after the
  // coach list arrives.
  const [compareCoachId, setCompareCoachId] = useState<string | null | undefined>(
    () => {
      const stored = readStoredValue(COMPARE_COACH_STORAGE_KEY);
      if (stored === null) {
        return undefined;
      }
      return stored || null;
    },
  );

  const coachProfiles = currentFacility.data?.coachProfiles;
  const sortedCoaches = useMemo(
    () => sortCoachesForCalendar(coachProfiles || []),
    [coachProfiles],
  );

  // A coach compares themselves. An admin and a scheduler compare the first
  // coach of the facility. After a coach leaves the facility, the column shows
  // the default coach again, because the header of the column contains the
  // selector and the view always shows one coach.
  //
  // The facility needs the feature for this choice. With the feature off the
  // page draws no coach column, so it stores nothing.
  useEffect(() => {
    if (!resourceManagementEnabled || sortedCoaches.length === 0) {
      return;
    }
    const known =
      compareCoachId &&
      sortedCoaches.some((coach) => coach.id === compareCoachId);
    if (known) {
      return;
    }
    const defaultCoach =
      user.role === RoleType.COACH
        ? sortedCoaches.find((coach) => coach.id === user.id)
        : sortedCoaches[0];
    const nextCoachId = defaultCoach?.id ?? sortedCoaches[0].id;
    setCompareCoachId(nextCoachId);
    writeStoredValue(COMPARE_COACH_STORAGE_KEY, nextCoachId);
  }, [
    resourceManagementEnabled,
    sortedCoaches,
    compareCoachId,
    user.role,
    user.id,
  ]);

  const onResourceViewChange = useCallback((nextResourceView: boolean) => {
    setStoredResourceView(nextResourceView);
    storeResourceView(nextResourceView);
  }, []);

  const onCompareCoachChange = useCallback((coachId: string) => {
    setCompareCoachId(coachId);
    writeStoredValue(COMPARE_COACH_STORAGE_KEY, coachId);
  }, []);

  if (!calendarDate && !currentFacility.pending) {
    setCalendarDate(DateTime.now().setZone(timeZone).startOf("day"));
  }

  const onNextCalendar = useCallback(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) {
      return;
    }

    calendarApi.next();
    setCalendarDate(
      DateTime.fromJSDate(calendarApi.view.activeStart, { zone: timeZone }),
    );
  }, [timeZone]);

  const onPrevCalendar = useCallback(() => {
    const calendarApi = calendarRef.current?.getApi();
    if (!calendarApi) {
      return;
    }

    calendarApi.prev();
    setCalendarDate(
      DateTime.fromJSDate(calendarApi.view.activeStart, { zone: timeZone }),
    );
  }, [timeZone]);

  const onDateChangeCalendar = useCallback(
    (date: DateTime) => {
      const calendarApi = calendarRef.current?.getApi();
      if (!calendarApi) {
        return;
      }

      const facilityDate = date
        .setZone(timeZone, { keepLocalTime: true })
        .startOf("day");
      setCalendarDate(facilityDate);
      calendarApi.gotoDate(facilityDate.toJSDate());
    },
    [timeZone],
  );

  const handleCalendarSettings = useCallback(() => {
    history.push("/calendar/pricing");
  }, [history]);

  const handleIntervalChange = useCallback(() => {}, []);

  const facilityDateFormat = useCallback(
    (date: DateTime, _view: string) =>
      formatFullWeekdayDate(date, preferences.dateFormat),
    [preferences.dateFormat],
  );

  const onViewChange = useCallback(
    (view: CalendarViewType) => {
      history.push(`/calendar?view=${view}`);
    },
    [history],
  );

  if (currentFacility.pending || !calendarDate) {
    return <Loading loadType="spinner" isLoading />;
  }

  // With the flag off, an admin and a scheduler see no view menu. With the
  // flag on, they see the two facility entries.
  const hideViewMenu = isAdminOrScheduler && !resourceManagementEnabled;

  return (
    <div className={styles.container}>
      <div
        id="calendar-element"
        className={`${styles.calendar_container} calendar-page-container`}
      >
        <CalendarToolbar
          date={calendarDate}
          dateFormat={facilityDateFormat}
          onNext={onNextCalendar}
          onPrev={onPrevCalendar}
          onDateChange={onDateChangeCalendar}
          handleCalendarSettings={user.role === RoleType.SCHEDULER ? undefined : handleCalendarSettings}
          initNewEvent={() => initNewEventOverrideRef.current?.()}
          calendarView="timeGridDay"
          currentView={preferences.calendarView}
          onViewChange={onViewChange}
          showFacilityCalendar={
            currentFacility.data?.coachProfiles?.length > 1 ||
            resourceManagementEnabled
          }
          isFacilityCalendarView
          showResourceCalendar={resourceManagementEnabled}
          isResourceCalendarView={resourceView}
          onFacilityViewChange={
            resourceManagementEnabled ? onResourceViewChange : undefined
          }
          facilityViewsOnly={isAdminOrScheduler}
          hideViewSelector={hideViewMenu}
        />

        <FacilityCalendarView
          calendarRef={calendarRef}
          preferences={preferences}
          eventElRef={eventElRef}
          initialDate={calendarDate}
          onIntervalChange={handleIntervalChange}
          initNewEventRef={initNewEventOverrideRef}
          resourceView={resourceView}
          compareCoachId={compareCoachId ?? null}
          compareCoaches={sortedCoaches}
          onCompareCoachChange={onCompareCoachChange}
        />
      </div>
    </div>
  );
}
